import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ComposeEmailContext,
  ComposeEmailContextType,
  openComposeProps
} from 'providersV2/compose-email/context';
import { postToOutlook } from './postToOutlook';

const OUTLOOK_SESSION_KEY = 'setvi-outlook-mode';

/**
 * Detects if the current session was initiated from an Outlook dialog.
 * Sets a sessionStorage flag on first /outlook/* visit so the override
 * persists when the user navigates to non-outlook routes (e.g. presentation details).
 */
export const useOutlookMode = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith('/outlook/')) {
      sessionStorage.setItem(OUTLOOK_SESSION_KEY, 'true');
    }
  }, [location.pathname]);

  // Check the pathname too — the sessionStorage flag is only written in an
  // effect, so it isn't set yet on the first render of an /outlook route.
  return (
    sessionStorage.getItem(OUTLOOK_SESSION_KEY) === 'true' ||
    location.pathname.startsWith('/outlook/')
  );
};

interface OutlookComposeProviderProps {
  children: ReactNode;
}

/**
 * Wraps children with a ComposeEmailContext override when in Outlook mode.
 * Any call to useComposeEmail().open() will send data back to the Outlook
 * add-in via Office.context.ui.messageParent() instead of opening
 * the internal compose window. Two flows are bridged:
 *
 * 1. Links flow (`linksData`) — resources/presentations dropdowns. Posted as
 *    `{ type: 'compose', links }`; the add-in builds SNT links and inserts them.
 *
 * 2. Body flow (`emailBody`, e.g. Quotes "Send Email") — such flows open the
 *    compose with a placeholder first, then re-open with `disableOpening =
 *    true` carrying the final HTML once their API resolves. We ignore the
 *    placeholder call and on the update call post `{ content, quoteShare }`.
 *    The body keeps the untracked preview link (1:1 with the web draft), and
 *    the add-in's OnMessageSend runtime mirrors the web's send-time work:
 *    beforeSend (preview→tracked swap) and afterSend (show-in-search +
 *    recording the final recipients) — using the forwarded `quoteShare`.
 */
const OutlookComposeProvider = ({ children }: OutlookComposeProviderProps) => {
  const isOutlookMode = useOutlookMode();

  const open = useCallback(
    (props: openComposeProps, disableOpening?: boolean) => {
      const { linksData, emailBody, quoteShare, quoteShares, products } = props;

      // Products flow (catalog-style payload) — e.g. Retention Intelligence's
      // "Send email" (top items) and the customer-products dialog footer. The
      // add-in renders its own product cards and inserts them at the cursor;
      // posting also closes this dialog window, so the whole web view goes away.
      if (products?.length) {
        postToOutlook({ products });
        return;
      }

      if (linksData) {
        const links = Array.isArray(linksData) ? linksData : [linksData];
        postToOutlook({ type: 'compose', links });
        return;
      }

      // The disableOpening call carries the final body (the flow's API has
      // resolved). For quote shares only the bare link is inserted into the
      // Outlook body (no "Hello / Here is your" wrapper); linkHtml itself is
      // not forwarded — the add-in only needs the share context.
      if (disableOpening && emailBody) {
        // Multi-quote selection: the body already holds one anchor per share,
        // so it's inserted as-is; the contexts ride along for OnMessageSend.
        if (quoteShares?.length) {
          postToOutlook({ content: emailBody, quoteShares });
          return;
        }
        if (quoteShare?.shareId) {
          const { linkHtml, ...shareContext } = quoteShare;
          postToOutlook({
            content: linkHtml || emailBody,
            quoteShare: shareContext
          });
          return;
        }
        postToOutlook({ content: emailBody });
      }
    },
    []
  );

  const contextValue = useMemo<ComposeEmailContextType>(
    () => ({
      open,
      close: () => {},
      values: { sendTo: [], textBody: '', subject: '', body: '' },
      links: [],
      editorNode: null,
      editorValues: {}
    }),
    [open]
  );

  if (!isOutlookMode) return <>{children}</>;

  return (
    <ComposeEmailContext.Provider value={contextValue}>
      {children}
    </ComposeEmailContext.Provider>
  );
};

export default OutlookComposeProvider;
